import { PERMISSIONS, PermissionsService } from "./../../../../services/permissions/permissions.service";
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ElementRef,
  ViewChild,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormArray,
} from "@angular/forms";
import moment from "moment";
import Swal from "sweetalert2";
import {
  ProgramBase,
  Menu,
  ReloadableCard,
} from "../../../../users-api";
import { Countries } from "../../../../countries";
import { AutoIdService } from "../../../../auto-id.service";
import { ProgramBaseLimitRedemption } from "../../../../../app/users-api/model/programBaseLimitRedemption";
import { ProgramsUtilService } from "../../../../../app/services/programs-util.service";
import { ReloadableCardModalComponent } from "./reloadable-cards/reloadable-card-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { MenuModalComponent } from "../menu/menu-modal.component";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {
  ReloadableCardGlobalConfig,
  ReloadableCardsGlobalsService,
} from "../../../../../app/services/reloadable-cards-globals.service";
import { KrecargasConfig, KrecargasService } from "../../../../../app/services/krecargas.service";
import { isNaN } from "lodash";

@Component({
  selector: "app-programs-editor",
  templateUrl: "./programs-editor.component.html",
  styleUrls: ["./programs-editor.component.scss"],
})
export class ProgramsEditorComponent implements OnInit, OnChanges {
  @ViewChild("minDateRedemptionLimits", { static: false })
  minDateRedemptionLimits: ElementRef;
  @ViewChild("maxDateRedemptionLimits", { static: false })
  maxDateRedemptionLimits: ElementRef;

  @Input() program: ProgramBase;
  @Input() mode = "create";

  @Output() onOK = new EventEmitter<ProgramBase>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onKrecargasUpdate = new EventEmitter<KrecargasConfig>();

  programForm: UntypedFormGroup;
  disableAutoID = false;
  autoIDValue = true;
  currencies = ["COP", "MXN", "CPL", "USD"];
  countries = Countries.COUNTRIES;
  appId = "";
  displayedMenuAndTabsColumns: string[] = ["label", "component", "actions"];
  displayedCardColumns: string[] = [
    "name",
    "sku",
    "price_factor",
    "price_offset_cents",
    "transfer_min_cents",
    "transfer_max_cents",
    "actions",
  ];

  global_cards: ReloadableCardGlobalConfig[] = [];
  reloadable_cards: (ReloadableCard & { name?: string; sku?: string; currency_code?: string; })[] = [];

  canViewKrecargas = false;
  canUpdateKrecargas = false;
  krecargasForm: UntypedFormGroup;

  data_redemption_limit: ProgramBaseLimitRedemption = {
    enabled: false,
    min_user_points_limit: 0,
    max_date: null,
    min_date: null,
    max_user_points_by_period: 0,
    period_type: null,
  };
  clon_data_redemption_limit: ProgramBaseLimitRedemption;
  today = moment().format("YYYY-MM-DDTHH:mm");
  // Por ahora solo 'month', a futuro podría tener: week, day
  periodTypeOptions = [{ value: "month", label: "Mensual" }];
  PERMISSIONS = PERMISSIONS;
  menu: Menu[] = [];
  tabs: Menu[] = [];

  personalDataFields = [
    { key: 'iddoc', label: 'Documento de Identidad' },
    { key: 'name', label: 'Nombres' },
    { key: 'last_name', label: 'Apellidos' },
    { key: 'birthday', label: 'Fecha de Nacimiento' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Teléfono' },
  ];

  personalDataOptions = [
    { value: 'optional', label: 'Opcional' },
    { value: 'required', label: 'Obligatorio' },
    { value: 'read-only', label: 'Solo lectura' },
  ];


  constructor(
    private fb: UntypedFormBuilder,
    private autoId: AutoIdService,
    private programsUtilService: ProgramsUtilService,
    private dialog: MatDialog,
    private reloadableCardsGlobalsService: ReloadableCardsGlobalsService,
    private krecargasService: KrecargasService,
    private permissionsService: PermissionsService,
  ) {
    this.programForm = this.fb.group({
      name: ["", Validators.required],
      program_id: [
        { value: "", disabled: true },
        [Validators.required, Validators.pattern(/^\w+$/)],
      ],
      auto_id: true,
      country: ["", [Validators.required, Validators.pattern(/^[A-Z]{2}$/)]],
      points_account_type: [
        "",
        [Validators.required, Validators.pattern(/^\w+$/)],
      ],
      budget: this.fb.group({
        enabled: false,
        value: this.fb.group({
          amount: [{ value: 0, disabled: true }, [Validators.pattern(/^\d+$/)]],
          currency_code: [
            { value: "", disabled: true },
            [Validators.pattern(/^[A-Z]{3}$/)],
          ],
        }),
        start_datetime: { value: "", disabled: true },
        start_date: { value: "", disabled: true },
        start_time: { value: "", disabled: true },
      }),
      erp: this.fb.group({
        program_code: ["", Validators.required],
        document_type: "",
        consecutive_type: "",
        wharehouse: "",
      }),
      user_type_id: [""],
      user_type_label: [""],
      opened_sign_in: [false],
      disable_exchanges: false,
      nequi: this.fb.group({
        enabled: false,
        factor: [{ value: 0, disabled: true }],
        transfer_cost_cents: [
          { value: 0, disabled: true },
          [Validators.pattern(/^\d+$/)],
        ],
        transfer_tax_percent: [
          { value: 0, disabled: true },
          [Validators.pattern(/^\d+$/)],
        ],
        min_transfer_cents: [
          { value: 0, disabled: true },
          [Validators.pattern(/^\d+$/)],
        ],
        max_transfer_cents: [
          { value: 0, disabled: true },
          [Validators.pattern(/^\d+$/)],
        ],
        max_n_transfers: [
          { value: 0, disabled: true },
          [Validators.pattern(/^\d+$/)],
        ],
      }),
      redemption_limit: this.fb.group({
        enabled: false,
        min_user_points_limit: [0],
        min_date: [],
        max_date: [],
        max_user_points_by_period: [0],
        period_type: [null],
      }),
      reloadable_cards: this.fb.array([]),
      personal_data: this.fb.group({
        iddoc: ['optional'],
        name: ['optional'],
        last_name: ['optional'],
        birthday: ['optional'],
        email: ['optional'],
        phone: ['optional'],
      }),
    });

    const name = this.programForm.get("name");
    const auto_id = this.programForm.get("auto_id");
    const program_id = this.programForm.get("program_id");
    name.valueChanges.subscribe((value: string) => {
      if (auto_id.value && this.mode === "create") {
        program_id.setValue(this.autoId.buildAutoId(value));
      }
    });
    auto_id.valueChanges.subscribe((value: boolean) => {
      if (this.mode === "create") {
        if (value) {
          program_id.setValue(this.autoId.buildAutoId(name.value));
          program_id.disable();
          return;
        }
        program_id.enable();
      }
    });

    this.programForm
      .get("budget.enabled")
      .valueChanges.subscribe((enabled: boolean) => {
        const amount = this.programForm.get("budget.value.amount");
        const currency_code = this.programForm.get(
          "budget.value.currency_code"
        );
        const start_date = this.programForm.get("budget.start_date");
        const start_time = this.programForm.get("budget.start_time");
        if (enabled) {
          amount.enable();
          currency_code.enable();
          start_date.enable();
          start_time.enable();
          return;
        }
        amount.disable();
        currency_code.disable();
        start_date.disable();
        start_time.disable();
      });

    this.programForm
      .get("nequi.enabled")
      .valueChanges.subscribe((enabled: boolean) => {
        const factor = this.programForm.get("nequi.factor");
        const transfer_cost_cents = this.programForm.get(
          "nequi.transfer_cost_cents"
        );
        const transfer_tax_percent = this.programForm.get(
          "nequi.transfer_tax_percent"
        );
        const min_transfer_cents = this.programForm.get(
          "nequi.min_transfer_cents"
        );
        const max_transfer_cents = this.programForm.get(
          "nequi.max_transfer_cents"
        );
        const max_n_transfers = this.programForm.get("nequi.max_n_transfers");
        if (enabled) {
          factor.enable();
          transfer_cost_cents.enable();
          transfer_tax_percent.enable();
          min_transfer_cents.enable();
          max_transfer_cents.enable();
          max_n_transfers.enable();
          return;
        }
        factor.disable();
        transfer_cost_cents.disable();
        transfer_tax_percent.disable();
        min_transfer_cents.disable();
        max_transfer_cents.disable();
        max_n_transfers.disable();
      });

    this.programForm
      .get("redemption_limit.enabled")
      .valueChanges.subscribe((enable: boolean) => {
        const min_user_points_limit = this.programForm.get(
          "redemption_limit.min_user_points_limit"
        );
        const min_date = this.programForm.get("redemption_limit.min_date");
        const max_date = this.programForm.get("redemption_limit.max_date");
        const max_user_points_by_period = this.programForm.get(
          "redemption_limit.max_user_points_by_period"
        );
        const period_type = this.programForm.get(
          "redemption_limit.period_type"
        );
        if (enable) {
          min_user_points_limit.enable();
          min_date.enable();
          max_date.enable();
          max_user_points_by_period.enable();
          period_type.enable();
          return;
        }
        min_user_points_limit.disable();
        min_date.disable();
        max_date.disable();
        max_user_points_by_period.disable();
        period_type.disable();
      });
  }

  ngOnInit() {
    this.krecargasForm = this.fb.group({
      enabled: [false],
      factor: [{ value: 0, disabled: true }, [Validators.required, Validators.min(0)]],
      offset_cents: [{ value: 0, disabled: true }, [Validators.required, Validators.min(0)]],
    });
    // El checkbox "enabled" es la protección explícita: sin marcarlo, factor y
    // offset_cents quedan deshabilitados para que no se puedan editar por error.
    this.krecargasForm.get("enabled").valueChanges.subscribe((checked: boolean) => {
      const factor = this.krecargasForm.get("factor");
      const offsetCents = this.krecargasForm.get("offset_cents");
      if (checked) {
        factor.enable();
        offsetCents.enable();
      } else {
        factor.disable();
        offsetCents.disable();
      }
    });

    this.reloadableCardsGlobalsService
      .getConfigurableReloadableCards()
      .subscribe((cards) => {
        this.global_cards = cards;
        console.log('global_cards', this.global_cards);
        this.processReloadableCards();
      });

    this.permissionsService.hasPermission(PERMISSIONS.VIEW_KRECARGAS).then((canView) => {
      this.canViewKrecargas = canView;
      if (!canView) return;
      this.permissionsService.hasPermission(PERMISSIONS.UPDATE_KRECARGAS).then((canUpdate) => {
        this.canUpdateKrecargas = canUpdate;
        if (!canUpdate) {
          this.krecargasForm.disable();
        }
      });
      // ngOnChanges corre antes que se resuelva este permiso, así que
      // loadKrecargasConfig() pudo haber salido temprano por canViewKrecargas en false.
      if (this.mode !== "create") {
        this.loadKrecargasConfig();
      }
    });
  }

  ngOnChanges() {
    const values = { ...this.program } as any;
    this.menu = (values.menu || []).sort((a, b) => a.weight - b.weight);
    this.tabs = (values.tabs || []).sort((a, b) => a.weight - b.weight);
    const { start_datetime } = values.budget;
    values.budget = {
      ...values.budget,
      start_date: moment(start_datetime).format("YYYY-MM-DD"),
      start_time: moment(start_datetime).format("hh:mm a"),
    };
    this.programForm.patchValue(values);
    if (this.program && this.program.personal_data) {
      this.programForm.get('personal_data').patchValue(this.program.personal_data);
    }

    this.appId = this.program.app_id;

    this.processReloadableCards();

    const autoId = this.programForm.get("auto_id");
    autoId.setValue(true);
    this.programForm.get("program_id").disable();
    if (this.mode !== "create") {
      autoId.disable();
    } else {
      autoId.enable();
    }
    if (this.mode != "create") {
      this.data_redemption_limit = this.program.redemption_limit
        ? this.program.redemption_limit
        : this.data_redemption_limit;
      this.clon_data_redemption_limit = { ...this.data_redemption_limit };
      this.setDatesLimits();
      this.loadKrecargasConfig();
    }
  }

  private buildKrecargasProgramId(): string {
    return `companies/${this.program.company_id}/program/${this.program.program_id}`;
  }

  private loadKrecargasConfig(): void {
    if (!this.canViewKrecargas || !this.program.app_id || !this.program.program_id) return;
    this.krecargasService
      .getConfig(this.buildKrecargasProgramId())
      .subscribe({
        next: (config) => {
          this.krecargasForm.patchValue({ enabled: true, factor: config.factor, offset_cents: config.offset_cents });
        },
        error: (err) => console.warn('No se pudo cargar la config de krecargas', err),
      });
  }

  processReloadableCards() {
    console.info('program.reloadable_cards', this.program.reloadable_cards);
    if (this.program && this.program.reloadable_cards) {
      this.reloadable_cards = this.program.reloadable_cards.map(
        (apiCard: ReloadableCard) => {
          const globalCard = this.global_cards.find(
            (gCard) => gCard.id === apiCard.card_id
          );
          return {
            ...apiCard,
            name:  globalCard?.name || '',
            sku: globalCard?.sku || '',
            currency_code: globalCard?.currency_code || '',
          };
        }
      );
    } else {
      this.reloadable_cards = [];
    }
    console.info('reloadable_cards', this.reloadable_cards);
    this.updateCardsFormArray();
  }

  cancelClicked() {
    this.onCancel.emit();
    return false;
  }

  openAddCardModal(): void {
    const dialogRef = this.dialog.open(ReloadableCardModalComponent, {
      width: "550px",
      data: { mode: "add" },
    });
    dialogRef.afterClosed().subscribe((result: ReloadableCard) => {
      if (result) {
        const globalCard = this.global_cards.find(
          (gCard) => gCard.id === result.card_id
        );
        this.reloadable_cards.push({
          ...result,
          name: globalCard ? globalCard.name : "Unknown",
          sku: globalCard ? globalCard.sku : "Unknown",
          currency_code: globalCard?.currency_code ?? '',
        });
        this.reloadable_cards = [...this.reloadable_cards];
        this.updateCardsFormArray();
      }
    });
  }

  deleteCard(index: number): void {
    this.reloadable_cards.splice(index, 1);
    this.reloadable_cards = [...this.reloadable_cards];
    this.updateCardsFormArray();
  }

  formatMonetary(s: Record<string, unknown>, k: string) {
    const v = s[k];
    if (typeof v !== 'number') return '';
    return `${v/100} ${s.currency_code}`;
  }

  updateCardsFormArray(): void {
    const cardsArray = this.programForm.get("reloadable_cards") as FormArray;
    cardsArray.clear();
    this.reloadable_cards.forEach((card) => {
      cardsArray.push(
        this.fb.group({
          card_id: [card.card_id, Validators.required],
          transfer_min_cents: [
            card.transfer_min_cents,
            [Validators.required, Validators.min(1)],
          ],
          transfer_max_cents: [card.transfer_max_cents],
          price_factor: [card.price_factor, Validators.required],
          price_offset_cents: [card.price_offset_cents, Validators.required],
        })
      );
    });
  }

  openEditCardModal(
    card: ReloadableCard & { name?: string; sku?: string },
    index: number
  ): void {
    const dialogRef = this.dialog.open(ReloadableCardModalComponent, {
      width: "550px",
      data: { mode: "edit", card },
    });
    dialogRef.afterClosed().subscribe((result: ReloadableCard) => {
      if (result) {
        const globalCard = this.global_cards.find(
          (gCard) => gCard.id === result.card_id
        );
        this.reloadable_cards[index] = {
          ...result,
          name: globalCard ? globalCard.name : "Unknown",
          sku: globalCard ? globalCard.sku : "Unknown",
          currency_code: globalCard?.currency_code ?? '',
        };
        this.reloadable_cards = [...this.reloadable_cards];
        this.updateCardsFormArray();
      }
    });
  }

  okClicked() {
    const valueCopy = { ...this.programForm.getRawValue() };
    valueCopy.reloadable_cards = this.reloadable_cards.map((card) => {
      const minCentsOk = !isNaN(card.transfer_min_cents) && card.transfer_min_cents >= 1;
      const factorOk = !isNaN(card.price_factor) && card.price_factor >= 0;
      const offsetOk = !isNaN(card.price_offset_cents) && card.price_offset_cents >= 0;
      if (!card.card_id || !minCentsOk || !factorOk || !offsetOk) {
        const msg = 'Tarjeta tiene formato incorrecto';
        console.error(msg, {card, minCentsOk, factorOk, offsetOk});
        throw new Error(msg);
      }
      return {
        card_id: card.card_id,
        transfer_min_cents: Number(card.transfer_min_cents),
        price_factor: Number(card.price_factor),
        price_offset_cents: Number (card.price_offset_cents),
        ...(
          !isNaN(card.transfer_max_cents)
          && card.transfer_max_cents >= card.transfer_min_cents ?
            {transfer_max_cents: Number(card.transfer_max_cents)} :
            {}
        ),
      };
    });
    delete valueCopy.auto_id;
    valueCopy.company_id = this.program.company_id;
    const { start_date, start_time, value } = valueCopy.budget;
    const { min_user_points_limit, max_user_points_by_period, period_type, enabled } = valueCopy.redemption_limit;
    valueCopy.redemption_limit.min_date = new Date(
      this.minDateRedemptionLimits.nativeElement.value
    );
    valueCopy.redemption_limit.max_date = new Date(
      this.maxDateRedemptionLimits.nativeElement.value
    );
    if (this.validateFormLimits(max_user_points_by_period, period_type)) {
      if (
        !this.minDateRedemptionLimits.nativeElement.value ||
        this.minDateRedemptionLimits.nativeElement.value == ""
      ) {
        delete valueCopy.redemption_limit.min_date;
      }
      if (
        !this.maxDateRedemptionLimits.nativeElement.value ||
        this.maxDateRedemptionLimits.nativeElement.value == ""
      ) {
        delete valueCopy.redemption_limit.max_date;
      }
      if (!min_user_points_limit) {
        delete valueCopy.redemption_limit.min_user_points_limit;
      }
      if (!max_user_points_by_period) {
        delete valueCopy.redemption_limit.max_user_points_by_period;
      }
      if (!period_type) {
        delete valueCopy.redemption_limit.period_type;
      }
    } else {
      return;
    }
    if (!value.amount) {
      delete valueCopy.budget.value;
    }
    if (start_date && start_time) {
      const parseDate = start_date.toDate
        ? moment(start_date.toDate()).format("YYYY-MM-DD")
        : start_date;
      valueCopy.budget.start_datetime = new Date(`${parseDate} ${start_time}`);
      delete valueCopy.budget.start_time;
      delete valueCopy.budget.start_date;
    } else {
      delete valueCopy.budget.start_datetime;
      delete valueCopy.budget.start_time;
      delete valueCopy.budget.start_date;
    }
    ["document_type", "consecutive_type", "wharehouse"].forEach((key) => {
      if (!valueCopy.erp[key]) {
        delete valueCopy.erp[key];
      }
    });
    valueCopy.app_id = this.appId;
    valueCopy.menu = this.menu;
    valueCopy.tabs = this.tabs;
    this.onOK.emit(valueCopy as ProgramBase);

    // Doble protección: "enabled" es el opt-in explícito del admin (este programa
    // sí es de México/Krecargas), y se exige que factor u offset_cents estén
    // dirty puntualmente (no el form completo: solo tildar el checkbox también
    // marca dirty al form, y eso por sí solo no implica que haya cargado valores reales).
    const krecargasValuesTouched = this.krecargasForm.get("factor").dirty
      || this.krecargasForm.get("offset_cents").dirty;
    if (this.canUpdateKrecargas && this.krecargasForm.get("enabled").value && krecargasValuesTouched) {
      const { factor, offset_cents } = this.krecargasForm.getRawValue();
      const krecargasBody: KrecargasConfig = {
        appId: this.appId,
        programId: this.buildKrecargasProgramId(),
        factor: Number(factor),
        offset_cents: Number(offset_cents),
      };
      console.log('[Krecargas] body:', krecargasBody);
      this.onKrecargasUpdate.emit(krecargasBody);
    }
  }

  onCheckboxChange(e) {
    if (e.checked) {
      this.setValidatorRequired("user_type_id", Validators.required);
      this.setValidatorRequired("user_type_label", Validators.required);
    } else {
      this.setValidatorRequired("user_type_id", "");
      this.setValidatorRequired("user_type_label", "");
      this.programForm["controls"]["user_type_id"].reset();
      this.programForm["controls"]["user_type_label"].reset();
    }
  }

  setValidatorRequired(field: string, validators: any) {
    this.programForm["controls"][field].setValidators(validators);
    this.programForm["controls"][field].updateValueAndValidity();
  }

  validateFormLimits(max_user_points_by_period?: number, period_type?: string) {
    let title = "Valide todos los campos";
    let valid = true;

    if (
      moment(this.minDateRedemptionLimits.nativeElement.value).isAfter(
        this.maxDateRedemptionLimits.nativeElement.value
      )
    ) {
      title = "La fecha inicial no debe superar la fecha final";
      valid = false;
    }
    if (
      (this.minDateRedemptionLimits.nativeElement.value &&
        !this.maxDateRedemptionLimits.nativeElement.value) ||
      (this.maxDateRedemptionLimits.nativeElement.value &&
        !this.minDateRedemptionLimits.nativeElement.value)
    ) {
      title = "Valida los campos de fecha";
      valid = false;
    }
    if (max_user_points_by_period > 0 && !period_type) {
      title = "Limites de redencion: Debe seleccionar el periodo máximo de puntos";
      valid = false;
    }
    if (period_type && !(max_user_points_by_period > 0)) {
      title = "Limites de redencion: El máximo de puntos en un periodo debe ser mayor a 0";
      valid = false;
    }

    if (!valid) {
      Swal.fire({
        icon: "error",
        title,
        confirmButtonColor: "#ff9800",
      });
    }
    return valid;
  }

  setDatesLimits() {
    if (this.data_redemption_limit) {
      if (
        this.data_redemption_limit.min_date &&
        this.data_redemption_limit.max_date
      ) {
        const min_date = moment(this.data_redemption_limit.min_date).format(
          "YYYY-MM-DDTHH:mm:ss"
        );
        const max_date = moment(this.data_redemption_limit.max_date).format(
          "YYYY-MM-DDTHH:mm:ss"
        );
        setTimeout(() => {
          this.minDateRedemptionLimits.nativeElement.value = min_date;
          this.maxDateRedemptionLimits.nativeElement.value = max_date;
        }, 250);
      }
    }
  }

  openAddMenuModal(): void {
    const dialogRef = this.dialog.open(MenuModalComponent, {
      width: "500px",
      data: { mode: "add", title: "Agregar Menú" },
    });

    dialogRef.afterClosed().subscribe((result: Menu) => {
      if (result) {
        this.menu.push({ ...result, weight: this.menu.length + 1 });
        this.menu = [...this.menu];
      }
    });
  }

  openEditMenuModal(menu: Menu): void {
    const dialogRef = this.dialog.open(MenuModalComponent, {
      width: "500px",
      data: { mode: "edit", menu, title: "Editar Menú",},
    });

    dialogRef.afterClosed().subscribe((result: Menu) => {
      if (result) {
        const index = this.menu.findIndex((m) => m.weight === menu.weight);
        this.menu[index] = result;
        this.updateMenuWeights();
        this.menu = [...this.menu];
      }
    });
  }

  deleteMenu(menu: Menu): void {
    const index = this.menu.findIndex((m) => m.weight === menu.weight);
    this.menu.splice(index, 1);
    this.menu = this.menu.map((m, i) => ({ ...m, weight: i + 1 }));
    this.menu = [...this.menu];
  }

  onDropMenu(event: CdkDragDrop<Menu[]>) {
    moveItemInArray(this.menu, event.previousIndex, event.currentIndex);
    this.updateMenuWeights();
  }

  updateMenuWeights(): void {
    this.menu = this.menu.map((menu, index) => ({
      ...menu,
      weight: index + 1,
    }));
  }

  openAddTabModal(): void {
    const dialogRef = this.dialog.open(MenuModalComponent, {
      width: "500px",
      data: { mode: "add", title: "Agregar Tab" },
    });

    dialogRef.afterClosed().subscribe((result: Menu) => {
      if (result) {
        this.tabs.push({ ...result, weight: this.tabs.length + 1 });
        this.tabs = [...this.tabs];
      }
    });
  }

  openEditTabModal(tab: Menu): void {
    const dialogRef = this.dialog.open(MenuModalComponent, {
      width: "500px",
      data: { mode: "edit", tab, title: "Editar Tab" },
    });

    dialogRef.afterClosed().subscribe((result: Menu) => {
      if (result) {
        const index = this.tabs.findIndex((t) => t.weight === tab.weight);
        this.tabs[index] = result;
        this.updateTabWeights();
        this.tabs = [...this.tabs];
      }
    });
  }

  deleteTab(tab: Menu): void {
    const index = this.tabs.findIndex((t) => t.weight === tab.weight);
    this.tabs.splice(index, 1);
    this.tabs = this.tabs.map((t, i) => ({ ...t, weight: i + 1 }));
    this.tabs = [...this.tabs];
  }

  onDropTab(event: CdkDragDrop<Menu[]>) {
    moveItemInArray(this.tabs, event.previousIndex, event.currentIndex);
    this.updateTabWeights();
  }

  updateTabWeights(): void {
    this.tabs = this.tabs.map((tab, index) => ({
      ...tab,
      weight: index + 1,
    }));
  }
}
